<template>
  <div>
    <slot name="prefix" />
    <naj-box :title="local.caseRelatedRequest.reconsiderationFromNonCaseParty.requestApplicant.originalTitle">
      <template v-if="isLawyer || isNajizCitizen">
        <div class="row mx-0">
          <v-col cols="12" md="6" lg="3">
            <naj-input
              :type-id="formFieldType.selectbox"
              :label="locale.socialIdType"
              :value.sync="formModel.socialIdType"
              :items="identityTypes"
              item-text="name"
              item-value="id"
              :disabled="viewMode || validating"
              validates="required"
            ></naj-input>
          </v-col>
          <v-col cols="12" md="6" lg="3">
            <naj-input
              :key="formKey"
              v-mask="identityNumberMask"
              :form-key="formKey"
              :technical-name="formKey"
              :type-id="formFieldType.text"
              :label="locale.socialId"
              :value.sync="socialId"
              :disabled="viewMode || validating || !formModel.socialIdType"
              :validates="`long|digits:10|isVerified:${isVerified}|required|socialId:${formModel.socialIdType}`"
              @input="identityNumberChanged"
            ></naj-input>
          </v-col>
          <v-col cols="12" md="12" lg="6">
            <naj-input
              :form-key="formKey"
              :technical-name="formKey"
              :label="locale.birthDate"
              :type-id="formFieldType.date"
              :value.sync="formModel.dateOfBirth"
              :disabled="viewMode || validating || isVerified || !formModel.socialIdType"
              @change="dateOfBirthChanged"
            ></naj-input>
          </v-col>
        </div>

        <div class="row mx-0">
          <v-col cols="12" md="6" lg="3">
            <naj-input
              :type-id="formFieldType.text"
              :label="locale.firstName"
              :technical-name="formKey"
              :value.sync="formModel.firstName"
              :disabled="viewMode || validating || isVerified || !formModel.socialIdType"
              :validates="'required'"
            ></naj-input>
          </v-col>
          <v-col cols="12" md="6" lg="3">
            <naj-input
              :type-id="formFieldType.text"
              :label="locale.fatherName"
              :value.sync="formModel.fatherName"
              :disabled="true"
            ></naj-input>
          </v-col>
          <v-col cols="12" md="6" :lg="viewMode || isVerified ? 3 : 2">
            <naj-input
              :type-id="formFieldType.text"
              :label="locale.thirdName"
              :value.sync="formModel.grandFatherName"
              :disabled="true"
            ></naj-input>
          </v-col>
          <v-col cols="12" md="6" :lg="viewMode || isVerified ? 3 : 2">
            <naj-input
              :type-id="formFieldType.text"
              :label="locale.familyName"
              :value.sync="formModel.familyName"
              :disabled="true"
            ></naj-input>
          </v-col>

          <v-col v-if="!viewMode && !isVerified" cols="12" md="6" :lg="2" class="d-flex align-center">
            <naj-button
              square
              inputs-alignment
              :loading="validating"
              :disabled="viewMode || isVerified || !socialId || (!formModel.dateOfBirth && !formModel.firstName)"
              @click="validateIdentityNumber"
            >
              {{ locale.validateIdentityNumber }}
            </naj-button>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon large v-bind="attrs" v-on="on">
                  mdi-information
                </v-icon>
              </template>
              <span>{{ locale.personVerify }}</span>
            </v-tooltip>
          </v-col>
        </div>
      </template>
      <template v-if="isFirmUser || isGovernmentalUser">
        <div>
          <v-col cols="12">
            <naj-data-table
              no-header
              :headers="firmHeader"
              :items="[organizationData]"
              :disable-items-per-page="true"
              no-margin-bottom
            >
            </naj-data-table>
          </v-col>
        </div>
      </template>
    </naj-box>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
const { mapActions: mapSysActions } = createNamespacedHelpers('SystemModule');
import NajBox from '@/components/shared/naj-box.vue';
import NajDataTable from '@/components/shared/naj-data-table.vue';
import { userType } from '@/helpers/enum';
import { partyTypeEnum } from '../../../../../helpers/enum';
import { uuidGenerator } from '@/helpers/utils';
import { identityTypeEnum } from '@/modules/bankruptcy/helpers/enum';

const { mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');

export default {
  name: 'RepresentativeApplicantForm',
  inject: ['$validator'],
  components: {
    NajBox,
    NajDataTable,
  },
  props: {
    formModel: {
      type: Object,
      required: true,
    },
    viewMode: {
      type: Boolean,
      required: true,
    },
    checkCommercialRegisterNumberStatus: {
      required: false,
      default: true,
      type: Boolean,
    },
  },
  data() {
    return {
      formKey: uuidGenerator(),
      validating: false,
      socialId: null,
      identityTypeEnum,
      identityTypes: null,
      headers: [],
      locale: this.local.caseRelatedRequest.reconsiderationFromNonCaseParty.requestApplicant,
      isIdentityVerified: false,
      reconsiderationFromNonCasePartyRole: 10009,
      firmHeader: [
        {
          value: 'organizationName',
          text: this.local.bankruptcy.identityVerificationForm.theName,
        },
        {
          value: 'unifiedNumber',
          text: this.local.bankruptcy.requestDetails.bankruptDebts.unifiedNumber,
        },
        {
          value: 'commercialNumber',
          text: this.local.bankruptcy.requestDetails.bankruptDebts.commercialRegistrationNumber,
        },
      ],
    };
  },
  computed: {
    ...mapGlobalState(['userInfo']),
    isVerified() {
      return this.isIdentityVerified;
    },
    identityNumberMask() {
      return !this.formModel.socialIdType ? '' : `${this.formModel.socialIdType}#########`;
    },
    isLawyer() {
      return this.userInfo && this.userInfo.userType === userType.najizLawyer;
    },
    isFirmUser() {
      return this.userInfo.isOrganizationUser && !this.isGovernmentalUser;
    },
    isGovernmentalUser() {
      return this.userInfo.loginType == 'governmental';
    },
    isNajizCitizen() {
      return this.userInfo && this.userInfo.userType === userType.najizCitizen;
    },
    organizationData() {
      if (!this.viewMode) {
        return {
          organizationName: this.userInfo.fullName,
          unifiedNumber: this.userInfo.nationalRecordNumber,
          commercialRegistrationNumber: this.userInfo.commercialRecordNumber,
          partyType: this.isGovernmentalUser ? partyTypeEnum.government : partyTypeEnum.firm,
        };
      } else {
        return {
          organizationName: this.formModel.firmName,
          unifiedNumber: this.formModel.unifiedNumber,
          commercialRegistrationNumber: this.formModel.commercialRegistrationNumber,
          partyType: this.isGovernmentalUser ? partyTypeEnum.government : partyTypeEnum.firm,
        };
      }
    },
  },
  mounted() {
    let socialTypes = {
      nationalId: 1,
      regularResidency: 2,
    };
    this.identityTypes = Object.keys(socialTypes).map(pt => ({
      id: identityTypeEnum[pt],
      name: this.local.bankruptcy.common[pt],
    }));

    if (this.viewMode) {
      this.socialId = this.formModel.socialId;
    }
  },
  methods: {
    ...mapSysActions(['checkMOIPersonalInfo']),
    resetVerification() {
      this.isIdentityVerified = false;
    },
    validateIdentityNumber() {
      const personalInfoRequest = {
        socialId: this.socialId,
        dateOfBirth: this.formModel.dateOfBirth,
        firstName: !this.formModel.dateOfBirth ? this.formModel.firstName : null,
      };
      this.validating = true;
      const props = ['firstName', 'fatherName', 'grandFatherName', 'familyName', 'nationalityCountryId', 'gender', 'dateOfBirth'];
      props.forEach((prop) => {
        this.$set(this.formModel, prop, undefined);
      });
      this.checkMOIPersonalInfo(personalInfoRequest)
        .then((personalInfo) => {
          this.toaster.success(this.local.common.verificationSucceeded, 2);
          props.forEach((prop) => {
            this.$set(this.formModel, prop, personalInfo[prop]);
          });

          this.isIdentityVerified = true;
          this.$set(this.formModel, 'socialId', this.socialId);
        })
        .catch(() => {
          this.toaster.error(this.local.personInformation.checkPersonalInfoError);
          this.resetVerification();
        })
        .finally(() => {
          this.validating = false;
        });
    },
    identityNumberChanged() {
      const props = ['firstName', 'fatherName', 'grandFatherName', 'familyName', 'nationalityCountryId', 'gender', 'dateOfBirth'];
      props.forEach((prop) => {
        this.$set(this.formModel, prop, undefined);
      });
      this.resetVerification();
    },
    dateOfBirthChanged() { },
  },
};
</script>

<style lang="scss"></style>
